import React, { useMemo, useEffect, useRef } from 'react';
import { Modal, Box, Typography, Button, Divider } from '@mui/material';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import DateRangeFilter from '../ui/date-range-filter';
import CalendarInput from '../ui/calendar-input';
import useApi from '../../hooks/use-api';
import { useSettings } from '../../providers/settings-provider';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';

type ConfigureModalProps = {
    open: boolean;
    onClose: () => void;
    selectedRowId: string | null;
    mode: 'move' | 'defer' | 'exclude-between';
    group: 'real-transactions' | 'invoices' | 'provisions';
    onSave?: (config: any) => void;
    initialData?: Record<string, any>;
    provisionData?: { description: string; amount: number; type: string; startDate?: string; endDate?: string; transactionDate?: string };
    transactions?: any[];
};

const toLocalDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseLocalDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
};

const ConfigureModal: React.FC<ConfigureModalProps> = ({ open, onClose, selectedRowId, mode, group, onSave, initialData, provisionData, transactions: passedTransactions }) => {
    const { formatDate, formatAmount } = useSettings();
    const [selectedRows, setSelectedRows] = React.useState<any[]>([]);
    const [deferRange, setDeferRange] = React.useState<{ start: Date; end: Date }>({ start: new Date(), end: new Date() });
    const [deferUntilDate, setDeferUntilDate] = React.useState<string>(new Date().toISOString().split('T')[0]);
    const [excludeRange, setExcludeRange] = React.useState<{ start: Date; end: Date }>({ start: new Date(), end: new Date() });
    const [movesByRowId, setMovesByRowId] = React.useState<Record<string, string>>({});
    const [originalMovesByRowId, setOriginalMovesByRowId] = React.useState<Record<string, string>>({});
    const [fetchTransactions, setFetchTransactions] = useApi();
    const [transactions, setTransactions] = React.useState<any[]>([]);
    const rowVirtualizerInstanceRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Reset state when modal opens or closes
    useEffect(() => {
        if (!open) {
            // Reset all state when modal closes
            setSelectedRows([]);
            setDeferRange({ start: new Date(), end: new Date() });
            setDeferUntilDate(new Date().toISOString().split('T')[0]);
            setExcludeRange({ start: new Date(), end: new Date() });
            setMovesByRowId({});
            setOriginalMovesByRowId({});
            setTransactions([]);
            return;
        }

        // Load initial data when modal opens
        if (open && initialData) {
            if (mode === 'defer' && (initialData.start || initialData.end)) {
                setDeferRange({
                    start: initialData.start instanceof Date ? initialData.start : parseLocalDate(initialData.start),
                    end: initialData.end instanceof Date ? initialData.end : parseLocalDate(initialData.end)
                });
            }
            if (mode === 'defer' && initialData.defer) {
                const dateStr = initialData.defer instanceof Date 
                    ? initialData.defer.toISOString().split('T')[0]
                    : initialData.defer;
                setDeferUntilDate(dateStr);
            }
            if (mode === 'exclude-between' && (initialData.start || initialData.end)) {
                setExcludeRange({
                    start: initialData.start instanceof Date ? initialData.start : parseLocalDate(initialData.start),
                    end: initialData.end instanceof Date ? initialData.end : parseLocalDate(initialData.end)
                });
            }
            if (mode === 'move' && initialData.movesByRowId) {
                setMovesByRowId(initialData.movesByRowId);
                setOriginalMovesByRowId(initialData.movesByRowId);
            }
        }
    }, [open, mode, initialData]);

    // Use passed-in transactions if available, otherwise fetch for provisions
    useEffect(() => {
        if (open && passedTransactions && passedTransactions.length > 0) {
            setTransactions(passedTransactions);
            return;
        }

        if (open && group === 'provisions' && selectedRowId) {
            // Extract ID from selectedRowId (e.g., "/provision-2077" -> "2077")
            const id = selectedRowId.includes('-') ? selectedRowId.split('-').pop() : selectedRowId;
            const { url, method } = buildApiRequestConfig(routes.provisionTemplate.fetchTransactionsById, [{ param: 'id', value: id }]);
            setFetchTransactions({ url, method });
        }
    }, [open, group, selectedRowId, setFetchTransactions, passedTransactions]);

    // Handle fetched transactions
    useEffect(() => {
        const { data, isLoading } = fetchTransactions;
        if (group === 'provisions' && data && !isLoading) {
            const mappedTransactions = data.data?.map((transaction: any) => ({
                id: transaction.sequenceId,
                guid: transaction.guid,
                date: transaction.date,
                amount: transaction.amount,
                description: transaction.description,
                categoryName: transaction.category?.name,
                bankAccountName: transaction.bankAccount?.accountName,
                isLinkable: transaction.isLinkable
            }));
            setTransactions(mappedTransactions);
        }
    }, [fetchTransactions, group]);

    // Initialize original move dates from transactions when in move mode without initial data
    useEffect(() => {
        if (mode === 'move' && transactions.length > 0 && Object.keys(originalMovesByRowId).length === 0) {
            const originalMoves: Record<string, string> = {};
            transactions.forEach(tx => {
                const txDate = typeof tx.date === 'string' ? tx.date : new Date(tx.date).toISOString().split('T')[0];
                originalMoves[tx.guid || tx.id] = txDate;
            });
            setOriginalMovesByRowId(originalMoves);
        }
    }, [mode, transactions]);

    // Auto-select transactions within exclude-between range
    useEffect(() => {
        if (mode === 'exclude-between' && transactions.length > 0) {
            const newSelectedRows: any[] = [];
            transactions.forEach(tx => {
                const txDate = new Date(tx.date);
                txDate.setHours(0, 0, 0, 0);
                
                const rangeStart = new Date(excludeRange.start);
                rangeStart.setHours(0, 0, 0, 0);
                
                const rangeEnd = new Date(excludeRange.end);
                rangeEnd.setHours(23, 59, 59, 999);
                
                // Check if transaction date falls within range
                if (txDate >= rangeStart && txDate <= rangeEnd) {
                    const id = tx.id || tx.guid;
                    if (id && !newSelectedRows.includes(id)) {
                        newSelectedRows.push(id);
                    }
                }
            });
            setSelectedRows(newSelectedRows);
        }
    }, [mode, transactions, excludeRange]);

    const handleApply = () => {
        if (!onSave) return;

        const config: any = {};

        if (mode === 'defer') {
            config.start = toLocalDateString(deferRange.start);
            config.end = toLocalDateString(deferRange.end);
            config.defer = deferUntilDate;
        } else if (mode === 'move') {
            // Only include moves where the date actually changed
            config.movesByRowId = Object.entries(movesByRowId).reduce((acc, [key, value]) => {
                if (value !== originalMovesByRowId[key]) {
                    acc[key] = value;
                }
                return acc;
            }, {} as Record<string, string>);
        } else if (mode === 'exclude-between') {
            config.start = toLocalDateString(excludeRange.start);
            config.end = toLocalDateString(excludeRange.end);
            config.selectedRows = selectedRows;
        }

        onSave(config);
        onClose();
    };

    const configureColumns: MRT_ColumnDef<any>[] = useMemo(() => {
        const baseColumns: MRT_ColumnDef<any>[] = [
            {
                accessorKey: 'date',
                header: 'Date',
                size: 150,
                Cell: ({ row }) => formatDate ? formatDate(row.original.date) : row.original.date,
            },
        ];

        if (mode === 'exclude-between') {
            if (group === 'real-transactions' || group === 'invoices' || group === 'provisions') {
                baseColumns.push({
                    accessorKey: 'amount',
                    header: 'Amount',
                    size: 100,
                    Cell: ({ row }) => {
                        const amount = row.original.amount;
                        const isNegative = amount < 0;
                        return (
                            <span style={{ color: isNegative ? 'var(--color-negative-amount)' : 'var(--color-default-text)' }}>
                                {formatAmount ? formatAmount(amount) : amount}
                            </span>
                        );
                    },
                }, {
                    accessorKey: 'description',
                    header: 'Description',
                    size: 200,
                });
            }
            baseColumns.push({
                accessorKey: 'checkbox',
                header: 'Exclude',
                size: 50,
                enableSorting: false,
                Cell: ({ row }) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <input
                            type="checkbox"
                            checked={selectedRows.includes(row.original.id || row.original.guid)}
                            style={{ accentColor: 'var(--color-primary)', cursor: 'not-allowed', opacity: 0.8 }}
                        />
                    </Box>
                ),
            });
        } 
        else if (mode === 'move') {
            baseColumns.push({
                accessorKey: 'moveDate',
                header: 'Move to Date',
                size: 250,
                enableSorting: false,
                Cell: ({ row }) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
                        <Box display="flex" gap={0.5} alignItems="center">
                            <Typography variant="caption" sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                                Move to
                            </Typography>
                            <CalendarInput
                                value={movesByRowId[row.original.guid || row.original.id] || new Date(row.original.date).toISOString().split('T')[0]}
                                onChange={(date) => setMovesByRowId(prev => ({ ...prev, [row.original.guid || row.original.id]: date }))}
                                placeholder="Select date"
                                sx={{ width: 120, height: 32, bgcolor: "var(--color-page-background)" }}
                                disableStartAdornment
                            />
                        </Box>
                    </Box>
                ),
            });
        }
        else {
            // Add details columns for defer mode
            baseColumns.push({
                accessorKey: 'amount',
                header: 'Amount',
                size: 100,
                Cell: ({ row }) => formatAmount ? formatAmount(row.original.amount) : row.original.amount,
            }, {
                accessorKey: 'description',
                header: 'Description',
                size: 250,
            });
        }

        return baseColumns;
    }, [mode, selectedRows, movesByRowId, group, formatDate, formatAmount]);

    const configureData = React.useMemo(() => {
        if (transactions && transactions.length > 0) {
            return transactions.map((tx, idx) => ({
                ...tx,
                id: tx.id || tx.guid || idx,
                guid: tx.guid || tx.id || idx,
            }));
        }
        // For one-time provisions with no transactions, show the single occurrence
        if (provisionData?.type === 'one-time') {
            const date = provisionData.transactionDate || provisionData.startDate || new Date().toISOString().split('T')[0];
            return [{
                id: selectedRowId,
                guid: selectedRowId,
                date: date,
                amount: provisionData.amount,
                description: provisionData.description
            }];
        }
        return [];
    }, [transactions, provisionData, selectedRowId]);

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: 'absolute' as const,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '80%',
                maxWidth: 800,
                maxHeight: '90vh',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 2,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column' as const,
                boxSizing: 'border-box' as const,
            }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {group === 'real-transactions' ? 'Real Transactions' : group === 'invoices' ? 'Invoices' : (provisionData?.description || 'Provisions')}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" >
                            {provisionData ? (provisionData.startDate && provisionData.endDate ? `${formatDate ? formatDate(provisionData.startDate) : provisionData.startDate} to ${formatDate ? formatDate(provisionData.endDate) : provisionData.endDate}` : (provisionData.transactionDate ? (formatDate ? formatDate(provisionData.transactionDate) : provisionData.transactionDate) : 'N/A')) : 'N/A'}
                        </Typography>
                        <Typography variant="h6" color={provisionData && provisionData.amount < 0 ? 'red' : 'black'}>
                            {provisionData ? formatAmount?.(provisionData.amount) : '$ 0.00'}
                        </Typography>
                        <Typography variant="h6">
                            {provisionData?.type || 'N/A'}
                        </Typography>
                    </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
                {
                    (mode === 'defer') &&
                    <Box display="flex" gap={2} alignItems="center" mb={1} justifyContent="flex-end">
                        <Typography variant="caption" sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                            Between
                        </Typography>
                        <DateRangeFilter
                            start={deferRange.start}
                            end={deferRange.end}
                            onChange={(start, end) => setDeferRange({ start, end })}
                            sx={{ height: 34, borderRadius: 1, width: 300 }}
                            separator='to'
                        />
                        <Typography variant="caption" sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                            Defer until
                        </Typography>
                        <CalendarInput
                            value={deferUntilDate}
                            onChange={setDeferUntilDate}
                            placeholder="Select date"
                            sx={{ height: 32, bgcolor: "var(--color-page-background)" }}
                        />
                    </Box>
                }
                {
                    (mode === 'exclude-between') &&
                    <Box display="flex" gap={2} alignItems="center" mb={1} justifyContent="flex-end">
                        <Typography variant="caption" sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                            Exclude between
                        </Typography>
                        <DateRangeFilter
                            start={excludeRange.start}
                            end={excludeRange.end}
                            onChange={(start, end) => setExcludeRange({ start, end })}
                            sx={{ height: 34, borderRadius: 1 }}
                        />
                    </Box>
                }
                <Box sx={{ position: 'relative', width: '100%', flex: 1, minHeight: 300 }}>
                    <MaterialReactTable
                        columns={configureColumns}
                        data={configureData}
                        enableRowVirtualization
                        rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
                        rowVirtualizerOptions={{ overscan: 10 }}
                        enablePagination={false}
                        enableTopToolbar={false}
                        enableBottomToolbar={false}
                        enableColumnActions={false}
                        initialState={{ density: 'compact' }}
                        muiTableContainerProps={{ sx: { height: 300, overflowY: 'auto', position: 'relative' }, ref: containerRef }}
                        muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                        muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 'auto', pt: 2 }}>
                    <Button onClick={onClose} variant="outlined">
                        Close
                    </Button>
                    <Button variant="contained" sx={{ color: '#fff' }} onClick={handleApply}>
                        Apply
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default ConfigureModal;
